import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Clock } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

export const ProfileAvatar = ({ src, username, testId, className = 'h-20 w-20' }) => (
  <Avatar className={`${className} ring-1 ring-white/15`} data-testid={testId}>
    <AvatarImage src={src ? (src.startsWith('/api/') ? `${BASE}${src}` : src) : undefined} alt={`${username}'s profile picture`} className="object-cover" data-testid={`${testId}-image`} />
    <AvatarFallback className="bg-primary/15 text-primary text-2xl" data-testid={`${testId}-fallback`}>{username?.slice(0, 2).toUpperCase() || <Camera />}</AvatarFallback>
  </Avatar>
);

export const ProfileStatus = ({ status, expiresAt, testId }) => {
  const [now, setNow] = useState(Date.now());
  const expiry = expiresAt ? new Date(expiresAt).getTime() : 0;
  useEffect(() => {
    setNow(Date.now());
    if (!expiry) return;
    const timer = setTimeout(() => setNow(Date.now()), Math.max(0, expiry - Date.now()) + 30);
    return () => clearTimeout(timer);
  }, [expiry]);
  if (!status || now >= expiry) return null;
  return <div className="border-l-2 border-amber-300/70 bg-amber-300/5 px-4 py-3 mt-4" data-testid={testId}><p className="text-sm whitespace-pre-wrap break-words">{status}</p><p className="text-[11px] text-muted-foreground mt-2 flex gap-1.5 items-center"><Clock size={13} /> Disappears {new Date(expiry).toLocaleString()}</p></div>;
};

export const AvatarEditor = ({ profile, username, onChange }) => {
  const [preview, setPreview] = useState(null);
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('');
  const [error, setError] = useState('');
  const controller = useRef(null);
  const busy = Boolean(phase);
  useEffect(() => () => { controller.current?.abort(); }, []);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);

  const upload = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    if (!['image/png', 'image/jpeg', 'image/webp', 'image/gif'].includes(file.type) || !file.size || file.size > 5 * 1024 * 1024) {
      setError('Choose a PNG, JPEG, WebP or GIF image, up to 5 MB.');
      return;
    }
    setPreview(URL.createObjectURL(file));
    setProgress(0);
    setPhase('Preparing upload');
    const abort = new AbortController();
    controller.current = abort;
    const options = { withCredentials: true, signal: abort.signal };
    let uploadId;
    try {
      const { data } = await axios.post(`${API}/profile/avatar/uploads`, { size: file.size }, options);
      uploadId = data.upload_id;
      const count = Math.ceil(file.size / data.chunk_size);
      for (let index = 0; index < count; index++) {
        setPhase(`Uploading part ${index + 1} of ${count}`);
        await axios.put(`${API}/profile/avatar/uploads/${uploadId}/chunks/${index}`, file.slice(index * data.chunk_size, (index + 1) * data.chunk_size), { ...options, headers: { 'Content-Type': 'application/octet-stream' }, onUploadProgress: event => setProgress(Math.round(85 * Math.min(file.size, index * data.chunk_size + event.loaded) / file.size)) });
      }
      setProgress(90);
      setPhase('Validating and saving your picture');
      const result = await axios.post(`${API}/profile/avatar/uploads/${uploadId}/complete`, {}, options);
      onChange(result.data);
      toast.success('Profile picture updated');
    } catch (err) {
      if (!axios.isCancel(err)) setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Upload failed. Please select your image to retry.');
      if (uploadId) await axios.delete(`${API}/profile/avatar/uploads/${uploadId}`, { withCredentials: true }).catch(() => {});
    } finally {
      setPhase('');
      setPreview(null);
      controller.current = null;
    }
  };

  const remove = async () => {
    setPhase('Removing picture');
    setError('');
    try {
      const { data } = await axios.delete(`${API}/profile/avatar`, { withCredentials: true });
      onChange(data);
      toast.success('Profile picture removed');
    } catch { setError('Could not remove your picture. Please try again.'); }
    finally { setPhase(''); }
  };

  return <div className="pb-6 mb-6 border-b border-white/10" data-testid="avatar-editor">
    <Label htmlFor="profile-picture">Profile picture / GIF</Label>
    <div className="flex flex-col sm:flex-row sm:items-center gap-5 mt-3">
      <ProfileAvatar src={preview || profile?.avatar_url} username={username} testId="profile-picture-preview" className="h-24 w-24" />
      <div className="flex-1 min-w-0 space-y-2"><Input id="profile-picture" type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={upload} disabled={busy} data-testid="profile-picture-input" className="file:text-foreground" /><p className="text-xs text-muted-foreground" data-testid="avatar-file-help">PNG, JPEG, WebP or animated GIF · up to 5 MB. Visible on your public profile.</p>{profile?.avatar_url && <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={remove} data-testid="remove-avatar-button">Remove picture</Button>}</div>
    </div>
    {busy && <div role="status" className="mt-3 space-y-2" data-testid="avatar-upload-progress"><p className="text-xs">{phase}… {controller.current && `${progress}%`}</p><progress className="w-full h-1 accent-rose-500" value={progress} max="100" aria-label="Upload progress" />{controller.current && progress < 90 && <Button type="button" variant="ghost" size="sm" onClick={() => controller.current?.abort()} data-testid="cancel-avatar-upload">Cancel upload</Button>}</div>}
    {error && <p role="alert" className="text-sm text-red-400 mt-3" data-testid="avatar-upload-error">{error}</p>}
  </div>;
};

export const StatusEditor = ({ profile, onChange }) => {
  const [status, setStatus] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { setStatus(profile?.status || ''); }, [profile?.status]);
  const save = async text => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/profile/status`, { status: text }, { withCredentials: true });
      onChange(data);
      setStatus(data.status);
      toast.success(text.trim() ? 'Status posted for 24 hours' : 'Status cleared');
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not update status'); }
    finally { setSaving(false); }
  };
  return <div className="pt-6 mt-6 border-t border-white/10" data-testid="status-editor"><Label htmlFor="profile-status">Your status · lasts 24 hours</Label><Input id="profile-status" value={status} maxLength={160} onChange={event => setStatus(event.target.value)} placeholder="What is on your mind today?" className="mt-2" data-testid="profile-status-input" /><p className="text-xs text-muted-foreground mt-2" data-testid="profile-status-help">{status.length}/160 · Posting starts a fresh 24 hours. Saving your bio does not extend it.</p><div className="flex gap-2 mt-3"><Button type="button" className="rounded-full" disabled={saving || !status.trim()} onClick={() => save(status)} data-testid="save-status-button">{saving ? 'Saving…' : 'Post status'}</Button><Button type="button" variant="ghost" disabled={saving || !profile?.status} onClick={() => save('')} data-testid="clear-status-button">Clear status</Button></div></div>;
};
